import { useEntityId } from '@/lib/offline/operations';
import { useMemo } from 'react';
import { ChevronRight, Plus, Truck } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useCamadaDetalle } from '@/lib/queries/camadas';
import { useEntregasDeCamada } from '@/lib/queries/entregasPienso';
import { useTiposPienso } from '@/lib/queries/catalogos';
import { fmtFecha, fmtInt } from '@/lib/format';
import { AppHeaderContent } from '@/components/AppHeaderContent';
import { useAuthStore } from '@/stores/auth';

/** Listado completo: edición en camada activa y solo lectura en camadas cerradas. */
export function CamadaEntregasPiensoPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const desdePienso = searchParams.get('from') === 'pienso';
  const { camadaId: camadaIdParam } = useParams<{ camadaId: string }>();
  const camadaId = useEntityId(camadaIdParam, 'camadas');
  const detalleQ = useCamadaDetalle(camadaId);
  const entregasQ = useEntregasDeCamada(camadaId);
  const tiposQ = useTiposPienso();
  const user = useAuthStore((state) => state.user);

  const entregas = useMemo(
    () => [...(entregasQ.data ?? [])].sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id),
    [entregasQ.data],
  );
  const tipoPorId = useMemo(
    () => new Map((tiposQ.data ?? []).map((tipo) => [tipo.id, tipo.codigo])),
    [tiposQ.data],
  );
  const camada = detalleQ.data?.camada;
  const loading = detalleQ.isLoading || entregasQ.isLoading || tiposQ.isLoading;
  const error =
    detalleQ.isError ||
    entregasQ.isError ||
    tiposQ.isError ||
    (!loading && (!camada || entregasQ.data == null || tiposQ.data == null));
  const canEdit = (user?.rol === 'admin' || user?.rol === 'ganadero') && camada && !camada.cerrada;
  const back = desdePienso ? 'pienso' : 'camada';

  return (
    <main className="cm-form-page mx-auto max-w-md px-4">
      <header className="ui-global-header" data-testid="app-header">
        <AppHeaderContent
          backTo={
            desdePienso ? '/pienso' : camadaId == null ? '/camadas' : `/camadas?camada=${camadaId}`
          }
          backLabel={desdePienso ? 'Pienso' : 'Camadas'}
        />
      </header>
      <h1 className="ui-page-title">{t('camada_detalle.piensos_listado_titulo')}</h1>
      {camada && (
        <p className="cm-form-context">
          {camada.codigo_lote}
          <span>
            {!loading &&
              !error &&
              t('camada_detalle.piensos_listado_subtitulo', { n: entregas.length })}
            {camada.cerrada ? ' · Finalizada' : ''}
          </span>
        </p>
      )}
      {error ? (
        <p role="alert" className="text-warning-text">
          {t('common.error')}
        </p>
      ) : loading ? (
        <p role="status" className="text-text-secondary">
          {t('common.loading')}
        </p>
      ) : (
        <>
          {canEdit && (
            <Link
              className="ui-primary"
              to={`/camadas/${camada!.id}/entregas-pienso/nueva?from=listado&back=${back}`}
            >
              <Truck size={20} aria-hidden />
              Registrar entrega de pienso
              <Plus size={18} aria-hidden />
            </Link>
          )}
          {!canEdit && (
            <p className="cm-form-note">
              {camada!.cerrada
                ? 'La camada está cerrada. Puedes consultar las entregas.'
                : 'Tu perfil permite consultar las entregas.'}
            </p>
          )}
          {entregas.length === 0 ? (
            <p className="cm-form-note">{t('camada_detalle.piensos_vacio')}</p>
          ) : (
            <ul className="cm-arrival-list" data-testid="camada-listado-piensos">
              {entregas.map((entrega) => {
                const contenido = (
                  <>
                    <Truck size={22} aria-hidden />
                    <span>
                      <span className="flex items-baseline justify-between gap-3">
                        <strong>{fmtFecha(entrega.fecha)}</strong>
                        <strong className="shrink-0 tabular-nums">
                          {t('camada_detalle.pienso_kg', { kg: fmtInt(entrega.kg) })}
                        </strong>
                      </span>
                      <small>
                        {tipoPorId.get(entrega.piensotipo_id) ??
                          t('camada_detalle.pienso_tipo_no_disponible')}
                        {entrega.num_albaran_procavi &&
                          ` · ${t('camada_detalle.pienso_albaran', { n: entrega.num_albaran_procavi })}`}
                      </small>
                      {entrega.observaciones && (
                        <small className="whitespace-pre-wrap break-words">
                          {entrega.observaciones}
                        </small>
                      )}
                    </span>
                    {canEdit && <ChevronRight size={18} aria-hidden />}
                  </>
                );
                return (
                  <li key={entrega.id}>
                    {!canEdit ? (
                      <div className="ui-menu-row cm-arrival-row">{contenido}</div>
                    ) : (
                      <Link
                        to={`/camadas/${camada!.id}/entregas-pienso/nueva?id=${entrega.id}&from=listado&back=${back}`}
                        className="ui-menu-row cm-arrival-row"
                        aria-label={`${t('pienso.titulo_editar')}: ${fmtFecha(entrega.fecha)}`}
                      >
                        {contenido}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </main>
  );
}
